import { useEffect, useState } from "react";

import PropTypes from "prop-types";
import { Controller, FormProvider, useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import { Badge, Box, Divider, Paper, Stack, Tab, Tabs } from "@mui/material";
import Grid from "@mui/material/Grid";

import {
  EntityTaxInformationSection,
  mapEntityTaxInformationToPayload,
} from "@/features/entity-tax-information";
import { StatusEnum, statusLabels } from "../../shared";
import { CenteredLoading, CustomInputField, CustomSelect } from "../../ui";
import { DEFAULT_VALUES, VALIDATION_SCHEMA } from "./company-form-constants";

// Each tab lists the form fields it owns so an invalid submit can (a) color the
// offending tab red and (b) jump the user straight to it — validation runs over
// the whole values object even while a tab is unmounted.
const TABS = [
  {
    label: "Informasi Umum",
    fields: ["name", "status"],
  },
  {
    label: "Pajak",
    fields: [
      "taxIdentificationNumber",
      "taxpayerName",
      "identityNumber",
      "defaultTaxIncluded",
      "taxInformationAddress",
      "taxInformationPostalCode",
      "taxInformationCity",
      "taxInformationProvince",
      "taxInformationCountry",
    ],
  },
];

export const CompanyForm = ({
  defaultValues,
  onSubmit,
  renderActionButton,
  isLoading = false,
}) => {
  const methods = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });
  const {
    control,
    register,
    reset,
    handleSubmit,
    formState,
    formState: { errors },
  } = methods;

  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    if (!defaultValues) {
      return;
    }

    reset({ ...DEFAULT_VALUES, ...defaultValues });
  }, [defaultValues, reset]);

  const onFormSubmit = async (formData) =>
    await onSubmit({
      name: formData.name,
      status: formData.status,
      entityTaxInformation: mapEntityTaxInformationToPayload(formData),
    });

  // A required field on a hidden tab can fail validation the user can't see —
  // jump to the first tab that has an error.
  const onInvalid = (formErrors) => {
    const erroredTab = TABS.findIndex(({ fields }) =>
      fields.some((field) => formErrors[field]),
    );
    if (erroredTab !== -1) {
      setTabIndex(erroredTab);
    }
  };

  return isLoading ? (
    <CenteredLoading sx={{ height: "100vh" }} />
  ) : (
    <FormProvider {...methods}>
      <Stack
        component="form"
        noValidate
        onSubmit={handleSubmit(onFormSubmit, onInvalid)}
        spacing={3}
      >
        <Paper sx={{ borderRadius: 2 }}>
          <Tabs
            value={tabIndex}
            onChange={(_, newValue) => setTabIndex(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 1 }}
          >
            {TABS.map(({ label, fields }) => {
              const hasError = fields.some((field) => errors[field]);
              return (
                <Tab
                  key={label}
                  label={
                    <Badge
                      color="error"
                      variant="dot"
                      invisible={!hasError}
                      sx={{ "& .MuiBadge-badge": { right: -8, top: 2 } }}
                    >
                      {label}
                    </Badge>
                  }
                  sx={{ color: hasError ? "error.main" : undefined }}
                />
              );
            })}
          </Tabs>
          <Divider />

          <Box sx={{ p: 3 }}>
            <Box sx={{ display: tabIndex === 0 ? "block" : "none" }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <CustomInputField
                    required
                    name="name"
                    label="Nama"
                    register={register}
                    helperText={errors.name?.message}
                    error={Boolean(errors.name)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Controller
                    control={control}
                    name="status"
                    render={({ field, fieldState }) => (
                      <CustomSelect
                        required
                        value={field.value}
                        onChange={field.onChange}
                        id="status"
                        label="Status"
                        options={Object.values(StatusEnum).map((value) => ({
                          value: value,
                          label: statusLabels[value],
                        }))}
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ display: tabIndex === 1 ? "block" : "none" }}>
              <EntityTaxInformationSection />
            </Box>
          </Box>
        </Paper>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          {renderActionButton({ isSubmitting: formState.isSubmitting })}
        </Box>
      </Stack>
    </FormProvider>
  );
};

CompanyForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
  renderActionButton: PropTypes.func,
  isLoading: PropTypes.bool,
};
